import React, { useState } from 'react';
import { supabase } from '../lib/supabase';

const categories = [
  { key: 'bug', label: 'Bug Report' },
  { key: 'feature', label: 'Feature Request' },
  { key: 'improvement', label: 'Improvement' },
  { key: 'other', label: 'Other' },
];

function FeedbackButton({ user, floating }) {
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState('bug');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async () => {
    if (!message.trim()) return;
    setSubmitting(true);
    try {
      await supabase.from('feedback').insert({
        user_id: user?.id || null,
        email: user?.email || null,
        category,
        message: message.trim(),
      });
      setSubmitted(true);
      setTimeout(() => {
        setOpen(false);
        setSubmitted(false);
        setMessage('');
        setCategory('bug');
      }, 1500);
    } catch (err) {
      console.error('Failed to submit feedback:', err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className={floating
          ? "bg-copper-500 text-white px-4 py-3 rounded-full shadow-lg hover:bg-copper-600 hover:shadow-xl transition-all duration-200 flex items-center gap-2 font-medium"
          : "px-3 py-1.5 text-sm text-gray-700 hover:bg-cream-200 rounded transition-colors flex items-center gap-1.5"
        }
      >
        <svg className={floating ? "w-5 h-5" : "w-4 h-4"} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
        </svg>
        <span className={floating ? "hidden md:inline" : ""}>Feedback</span>
      </button>

      {/* Modal */}
      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-navy-900/40"
            onClick={() => { if (!submitting) setOpen(false); }}
          />

          {/* Modal Content */}
          <div className="relative bg-cream-50 border border-cream-300 rounded-xl shadow-xl w-full max-w-md mx-4 p-6">
            {submitted ? (
              <div className="text-center py-8">
                <svg className="w-12 h-12 text-copper-500 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <h3 className="text-lg font-serif font-semibold text-navy-900 mb-1">Thank you!</h3>
                <p className="text-sm text-gray-600">Your feedback has been submitted.</p>
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between mb-5">
                  <h3 className="text-lg font-serif font-semibold text-navy-900">Send Feedback</h3>
                  <button
                    onClick={() => setOpen(false)}
                    className="p-1 hover:bg-cream-200 rounded transition-colors text-gray-500"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                {/* Category */}
                <div className="mb-4">
                  <label className="block text-xs font-medium text-gray-700 mb-2">Category</label>
                  <div className="flex flex-wrap gap-2">
                    {categories.map(c => (
                      <button
                        key={c.key}
                        onClick={() => setCategory(c.key)}
                        className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                          category === c.key
                            ? 'bg-navy-900 text-white'
                            : 'bg-cream-200 text-gray-600 hover:bg-cream-300'
                        }`}
                      >
                        {c.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Message */}
                <div className="mb-5">
                  <label className="block text-xs font-medium text-gray-700 mb-2">Message</label>
                  <textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="Tell us what's on your mind..."
                    rows={4}
                    className="w-full px-3 py-2 border border-cream-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-copper-500 focus:border-copper-500 resize-none"
                  />
                </div>

                {/* Submit */}
                <button
                  onClick={handleSubmit}
                  disabled={!message.trim() || submitting}
                  className={`w-full py-2.5 text-sm font-semibold rounded-lg transition-colors ${
                    !message.trim() || submitting
                      ? 'bg-cream-300 text-gray-400 cursor-not-allowed'
                      : 'bg-copper-500 text-white hover:bg-copper-600'
                  }`}
                >
                  {submitting ? 'Submitting...' : 'Submit Feedback'}
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}

export default FeedbackButton;
